import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import AppPageTitle from "../../components/AppPageTitle";
import AppModal from "../../components/AppModal";
import { i18n } from "../../translate/i18n";
import apiV2 from "../../services/apiV2";
import toastError from "../../errors/toastError";
import { formatChannelLabel } from "../../components/BookingChannelFields";

function pad(n) {
	return String(n).padStart(2, "0");
}

function toDateInputValue(d) {
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDateTime(raw) {
	if (!raw) return "—";
	if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
		const [y, m, day] = raw.split("-").map(Number);
		return new Date(y, m - 1, day).toLocaleDateString();
	}
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return raw;
	return d.toLocaleString(undefined, {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatSourceLabel(row) {
	const name = String(row?.createdByName || "").trim();
	if (name) return name;
	const value = String(row?.source || "").toLowerCase();
	if (value === "ai") return i18n.t("googleAgendaReport.sourceAi");
	if (value === "manual") return i18n.t("googleAgendaReport.sourceManual");
	if (value === "synced") return i18n.t("googleAgendaReport.sourceSynced");
	return value || "—";
}

function statusLabel(status) {
	const value = String(status || "scheduled").toLowerCase();
	if (value === "completed") return i18n.t("googleAgendaReport.statusCompleted");
	if (value === "cancelled") return i18n.t("googleAgendaReport.statusCancelled");
	if (value === "postponed") return i18n.t("googleAgendaReport.statusPostponed");
	return i18n.t("googleAgendaReport.statusScheduled");
}

function statusBadgeClass(status) {
	const value = String(status || "scheduled").toLowerCase();
	if (value === "completed") {
		return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300";
	}
	if (value === "cancelled") {
		return "bg-rose-500/15 text-rose-700 dark:text-rose-300";
	}
	if (value === "postponed") {
		return "bg-amber-500/15 text-amber-800 dark:text-amber-300";
	}
	return "bg-sky-500/15 text-sky-800 dark:text-sky-300";
}

function defaultRange() {
	const now = new Date();
	const from = new Date(now.getFullYear(), now.getMonth(), 1);
	const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
	return {
		from: toDateInputValue(from),
		to: toDateInputValue(to),
	};
}

function wideRange() {
	const now = new Date();
	const from = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
	const to = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
	return {
		from: toDateInputValue(from),
		to: toDateInputValue(to),
	};
}

function normalizeText(value) {
	return String(value || "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.trim();
}

function normalizePhone(value) {
	return String(value || "").replace(/\D/g, "");
}

function toLocalInputValue(date) {
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
		date.getHours()
	)}:${pad(date.getMinutes())}`;
}

function toLocalInputFromRaw(raw) {
	if (!raw) return "";
	const d = new Date(raw);
	if (Number.isNaN(d.getTime())) return "";
	return toLocalInputValue(d);
}

function DetailItem({ label, children }) {
	return (
		<div className="min-w-0">
			<p className="mb-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			<div className="text-sm text-default-900 dark:text-default-100">{children || "—"}</div>
		</div>
	);
}

function CountCard({ label, value }) {
	return (
		<div className="rounded-xl border border-default-200 bg-card px-3 py-2.5 dark:border-default-700 dark:bg-default-900">
			<p className="mb-0.5 text-[0.7rem] font-semibold uppercase tracking-wide text-default-500 dark:text-default-400">
				{label}
			</p>
			<p className="mb-0 text-xl font-bold text-default-900 dark:text-default-50">{value}</p>
		</div>
	);
}

export default function GoogleAgendaReport() {
	const history = useHistory();
	const location = useLocation();
	const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

	const queryContactId = query.get("contactId") || "";
	const queryName = query.get("name") || "";
	const queryPhone = query.get("phone") || "";
	const queryTicket = query.get("ticket") || "";
	const hasContactFocus = Boolean(queryContactId || queryName || queryPhone);

	const initial = useMemo(
		() => (hasContactFocus ? wideRange() : defaultRange()),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const [from, setFrom] = useState(initial.from);
	const [to, setTo] = useState(initial.to);
	const [statusFilter, setStatusFilter] = useState("all");
	const [searchName, setSearchName] = useState(queryName);
	const [searchTicket, setSearchTicket] = useState(queryTicket);
	const [searchPhone, setSearchPhone] = useState(queryPhone);
	const [searchBookedBy, setSearchBookedBy] = useState("");
	const [contactIdFilter, setContactIdFilter] = useState(queryContactId);
	const [allRows, setAllRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [connected, setConnected] = useState(null);
	const [selected, setSelected] = useState(null);
	const [editStatus, setEditStatus] = useState("scheduled");
	const [editReason, setEditReason] = useState("");
	const [editStart, setEditStart] = useState("");
	const [editEnd, setEditEnd] = useState("");
	const [savingStatus, setSavingStatus] = useState(false);

	useEffect(() => {
		setSearchName(queryName);
		setSearchPhone(queryPhone);
		setSearchTicket(queryTicket);
		setContactIdFilter(queryContactId);
		if (queryContactId || queryName || queryPhone) {
			const wide = wideRange();
			setFrom(wide.from);
			setTo(wide.to);
			setStatusFilter("all");
		}
	}, [queryContactId, queryName, queryPhone, queryTicket]);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const statusRes = await apiV2.get("/google-calendar/status");
			const ok = Boolean(statusRes.data?.connected);
			setConnected(ok);
			if (!ok) {
				setAllRows([]);
				return;
			}
			const timeMin = new Date(`${from}T00:00:00`).toISOString();
			const timeMax = new Date(`${to}T23:59:59`).toISOString();
			const { data } = await apiV2.get("/google-calendar/report", {
				params: { timeMin, timeMax, status: "all" },
			});
			setAllRows(Array.isArray(data?.events) ? data.events : []);
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("googleAgendaReport.loadError"));
			setAllRows([]);
		} finally {
			setLoading(false);
		}
	}, [from, to]);

	useEffect(() => {
		load();
	}, [load]);

	const counts = useMemo(() => {
		const result = {
			total: allRows.length,
			scheduled: 0,
			completed: 0,
			cancelled: 0,
			postponed: 0,
		};
		allRows.forEach((row) => {
			const status = String(row?.status || "scheduled").toLowerCase();
			if (status === "completed") result.completed += 1;
			else if (status === "cancelled") result.cancelled += 1;
			else if (status === "postponed") result.postponed += 1;
			else result.scheduled += 1;
		});
		return result;
	}, [allRows]);

	const rows = useMemo(() => {
		const nameQ = normalizeText(searchName);
		const ticketQ = String(searchTicket || "").replace(/\D/g, "");
		const phoneQ = normalizePhone(searchPhone);
		const bookedByQ = normalizeText(searchBookedBy);
		const contactIdQ = String(contactIdFilter || "").trim();

		return allRows.filter((row) => {
			const status = String(row?.status || "scheduled").toLowerCase();
			if (statusFilter !== "all" && status !== statusFilter) return false;

			if (contactIdQ) {
				const rowContactId = row?.contactId != null ? String(row.contactId) : "";
				if (rowContactId && rowContactId !== contactIdQ) return false;
				if (!rowContactId) {
					const nameOk = !nameQ || normalizeText(row.contactName).includes(nameQ);
					const phoneOk = !phoneQ || normalizePhone(row.contactPhone).includes(phoneQ);
					if (!nameOk && !phoneOk) return false;
				}
			}

			if (nameQ && !normalizeText(row.contactName).includes(nameQ)) return false;
			if (ticketQ) {
				const ticket = row?.ticketId != null ? String(row.ticketId) : "";
				if (!ticket.includes(ticketQ)) return false;
			}
			if (phoneQ && !normalizePhone(row.contactPhone).includes(phoneQ)) return false;
			if (bookedByQ && !normalizeText(formatSourceLabel(row)).includes(bookedByQ)) {
				return false;
			}
			return true;
		});
	}, [
		allRows,
		statusFilter,
		searchName,
		searchTicket,
		searchPhone,
		searchBookedBy,
		contactIdFilter,
	]);

	const openPreview = (row) => {
		setSelected(row);
		setEditStatus(String(row?.status || "scheduled").toLowerCase() || "scheduled");
		setEditReason(row?.statusReason || "");
		setEditStart(toLocalInputFromRaw(row?.start));
		setEditEnd(toLocalInputFromRaw(row?.end));
	};

	const closePreview = () => {
		if (savingStatus) return;
		setSelected(null);
	};

	const clearPersonFocus = () => {
		setContactIdFilter("");
		setSearchName("");
		setSearchPhone("");
		setSearchTicket("");
		setSearchBookedBy("");
		history.replace("/google-agenda/relatorio");
	};

	const saveStatus = async () => {
		if (!selected || savingStatus) return;
		if (editStatus === "postponed") {
			if (!editStart || !editEnd) {
				toast.error(i18n.t("googleAgendaReport.postponeRequired"));
				return;
			}
			if (new Date(editEnd).getTime() <= new Date(editStart).getTime()) {
				toast.error(i18n.t("googleAgendaReport.postponeInvalidRange"));
				return;
			}
		}
		setSavingStatus(true);
		try {
			const bookingId = selected.id || 0;
			const payload = {
				status: editStatus,
				reason: editReason,
				googleEventId: selected.googleEventId || "",
				title: selected.title || "",
				description: selected.description || "",
				start: selected.start || "",
				end: selected.end || "",
				contactName: selected.contactName || "",
				contactPhone: selected.contactPhone || "",
				ticketId: selected.ticketId || undefined,
				source: selected.source || "",
				createdByName: selected.createdByName || "",
			};
			if (editStatus === "postponed") {
				payload.start = new Date(editStart).toISOString();
				payload.end = new Date(editEnd).toISOString();
			}
			const { data } = await apiV2.put(`/google-calendar/bookings/${bookingId}/status`, payload);
			const booking = data?.booking;
			toast.success(i18n.t("googleAgendaReport.statusSaved"));
			setSelected((prev) =>
				prev
					? {
							...prev,
							id: booking?.id || prev.id,
							status: booking?.status || editStatus,
							statusReason: booking?.statusReason ?? editReason,
							statusChangedByName: booking?.statusChangedByName || "",
							statusChangedAt: booking?.statusChangedAt || new Date().toISOString(),
							start: booking?.startsAt || payload.start || prev.start,
							end: booking?.endsAt || payload.end || prev.end,
					  }
					: prev
			);
			await load();
		} catch (err) {
			toastError(err);
			toast.error(i18n.t("googleAgendaReport.statusSaveError"));
		} finally {
			setSavingStatus(false);
		}
	};

	const personLabel = searchName || queryName || (contactIdFilter ? `#${contactIdFilter}` : "");

	return (
		<div className="app-admin-page">
			<div className="mb-4 flex flex-wrap items-start justify-between gap-3">
				<div>
					<AppPageTitle
						title={
							personLabel
								? `${i18n.t("googleAgendaReport.title")} — ${personLabel}`
								: i18n.t("googleAgendaReport.title")
						}
						subtitle={i18n.t("googleAgendaReport.subtitle")}
					/>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Link
						to="/google-agenda"
						className="btn btn-sm border border-default-300 bg-transparent font-medium text-default-800 hover:bg-default-100 dark:border-default-600 dark:text-default-100 dark:hover:bg-default-800"
					>
						{i18n.t("googleAgendaReport.back")}
					</Link>
					<button
						type="button"
						className="btn btn-sm bg-primary text-white hover:bg-primary/90"
						onClick={load}
						disabled={loading}
					>
						{i18n.t("googleAgendaReport.refresh")}
					</button>
				</div>
			</div>

			{connected === false ? (
				<div className="rounded-xl border border-default-200 bg-card p-6 text-center dark:border-default-700 dark:bg-default-900">
					<p className="mb-3 text-sm text-default-600 dark:text-default-300">
						{i18n.t("googleAgendaReport.notConnected")}
					</p>
					<Link to="/integrations" className="btn btn-sm bg-primary text-white">
						{i18n.t("googleAgenda.goToIntegrations")}
					</Link>
				</div>
			) : (
				<>
					<div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
						<CountCard label={i18n.t("googleAgendaReport.countTotal")} value={counts.total} />
						<CountCard
							label={i18n.t("googleAgendaReport.statusScheduled")}
							value={counts.scheduled}
						/>
						<CountCard
							label={i18n.t("googleAgendaReport.statusCompleted")}
							value={counts.completed}
						/>
						<CountCard
							label={i18n.t("googleAgendaReport.statusCancelled")}
							value={counts.cancelled}
						/>
						<CountCard
							label={i18n.t("googleAgendaReport.statusPostponed")}
							value={counts.postponed}
						/>
					</div>

					<div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl border border-default-200 bg-card p-3 dark:border-default-700 dark:bg-default-900">
						<label className="flex flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.from")}
							<input
								type="date"
								className="form-input"
								value={from}
								onChange={(e) => setFrom(e.target.value)}
							/>
						</label>
						<label className="flex flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.to")}
							<input
								type="date"
								className="form-input"
								value={to}
								onChange={(e) => setTo(e.target.value)}
							/>
						</label>
						<label className="flex flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.statusFilter")}
							<select
								className="form-select min-w-[10rem]"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<option value="all">{i18n.t("googleAgendaReport.statusAll")}</option>
								<option value="scheduled">{i18n.t("googleAgendaReport.statusScheduled")}</option>
								<option value="completed">{i18n.t("googleAgendaReport.statusCompleted")}</option>
								<option value="postponed">{i18n.t("googleAgendaReport.statusPostponed")}</option>
								<option value="cancelled">{i18n.t("googleAgendaReport.statusCancelled")}</option>
							</select>
						</label>
						<label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.searchName")}
							<input
								type="text"
								className="form-input"
								value={searchName}
								onChange={(e) => setSearchName(e.target.value)}
								placeholder={i18n.t("googleAgendaReport.searchName")}
							/>
						</label>
						<label className="flex min-w-[7rem] flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.searchTicket")}
							<input
								type="text"
								className="form-input"
								value={searchTicket}
								onChange={(e) => setSearchTicket(e.target.value)}
								placeholder="#"
							/>
						</label>
						<label className="flex min-w-[8rem] flex-1 flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.searchPhone")}
							<input
								type="text"
								className="form-input"
								value={searchPhone}
								onChange={(e) => setSearchPhone(e.target.value)}
								placeholder={i18n.t("googleAgendaReport.searchPhone")}
							/>
						</label>
						<label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-xs text-default-600 dark:text-default-300">
							{i18n.t("googleAgendaReport.searchBookedBy")}
							<input
								type="text"
								className="form-input"
								value={searchBookedBy}
								onChange={(e) => setSearchBookedBy(e.target.value)}
								placeholder={i18n.t("googleAgendaReport.searchBookedBy")}
							/>
						</label>
						<button
							type="button"
							className="btn btn-sm border border-primary/40 bg-primary/15 font-semibold text-primary hover:bg-primary/25 dark:border-primary/50 dark:bg-primary/20 dark:text-primary dark:hover:bg-primary/30"
							onClick={load}
							disabled={loading}
						>
							{i18n.t("googleAgendaReport.filter")}
						</button>
						{(contactIdFilter ||
							searchName ||
							searchPhone ||
							searchTicket ||
							searchBookedBy) && (
							<button
								type="button"
								className="btn btn-sm border border-default-300 bg-transparent dark:border-default-600"
								onClick={clearPersonFocus}
							>
								{i18n.t("googleAgendaReport.clearFilters")}
							</button>
						)}
					</div>

					<div className="overflow-auto rounded-xl border border-default-200 bg-card dark:border-default-700 dark:bg-default-900">
						<table className="min-w-full border-collapse text-left text-sm text-default-800 dark:text-default-200">
							<thead className="bg-default-100 text-xs uppercase tracking-wide text-default-600 dark:bg-default-800 dark:text-default-300">
								<tr>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colTitle")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colStatus")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colDescription")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colStart")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colEnd")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colTicket")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colName")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colPhone")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colChannel")}
									</th>
									<th className="border-b border-default-200 px-3 py-2.5 font-semibold dark:border-default-700">
										{i18n.t("googleAgendaReport.colSource")}
									</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td
											colSpan={10}
											className="px-3 py-8 text-center text-default-500 dark:text-default-400"
										>
											{i18n.t("googleAgendaReport.loading")}
										</td>
									</tr>
								) : rows.length === 0 ? (
									<tr>
										<td
											colSpan={10}
											className="px-3 py-8 text-center text-default-500 dark:text-default-400"
										>
											{i18n.t("googleAgendaReport.empty")}
										</td>
									</tr>
								) : (
									rows.map((row, index) => (
										<tr
											key={row.googleEventId || row.id || index}
											className="cursor-pointer odd:bg-transparent even:bg-default-50 hover:bg-primary/5 dark:even:bg-default-800/50 dark:hover:bg-primary/10"
											onClick={() => openPreview(row)}
										>
											<td className="border-b border-default-100 px-3 py-2.5 align-top font-medium text-default-900 dark:border-default-800 dark:text-default-50">
												{row.title || "—"}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800">
												<span
													className={`inline-flex rounded-md px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(
														row.status
													)}`}
												>
													{statusLabel(row.status)}
												</span>
											</td>
											<td className="max-w-xs border-b border-default-100 px-3 py-2.5 align-top text-default-700 dark:border-default-800 dark:text-default-300">
												{row.description || "—"}
											</td>
											<td className="whitespace-nowrap border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{formatDateTime(row.start)}
											</td>
											<td className="whitespace-nowrap border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{formatDateTime(row.end)}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{row.ticketId != null ? `#${row.ticketId}` : "—"}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{row.contactName || "—"}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{row.contactPhone || "—"}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												{formatChannelLabel(
													row.acquisitionChannel,
													row.referredBy
												)}
											</td>
											<td className="border-b border-default-100 px-3 py-2.5 align-top dark:border-default-800 dark:text-default-200">
												<span
													className={
														String(row.source).toLowerCase() === "ai"
															? "rounded-md bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary"
															: "rounded-md bg-default-150 px-2 py-0.5 text-xs font-semibold text-default-700 dark:bg-default-800 dark:text-default-200"
													}
												>
													{formatSourceLabel(row)}
												</span>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</>
			)}

			<AppModal
				open={Boolean(selected)}
				onClose={closePreview}
				title={selected?.title || i18n.t("googleAgendaReport.previewTitle")}
				subtitle={i18n.t("googleAgendaReport.previewSubtitle")}
				maxWidth={640}
				footer={
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div>
							{selected?.ticketId != null ? (
								<button
									type="button"
									className="btn btn-sm border border-primary/40 bg-primary/15 font-semibold text-primary hover:bg-primary/25 dark:border-primary/50 dark:bg-primary/20 dark:hover:bg-primary/30"
									onClick={() => {
										const id = selected.ticketId;
										closePreview();
										history.push(`/tickets/${id}`);
									}}
									disabled={savingStatus}
								>
									{i18n.t("googleAgendaReport.openTicket")} #{selected.ticketId}
								</button>
							) : null}
						</div>
						<div className="flex flex-wrap justify-end gap-2">
							<button
								type="button"
								className="btn btn-sm border border-default-300 bg-transparent dark:border-default-600"
								onClick={closePreview}
								disabled={savingStatus}
							>
								{i18n.t("googleAgendaReport.close")}
							</button>
							<button
								type="button"
								className="btn btn-sm bg-primary text-white"
								onClick={saveStatus}
								disabled={savingStatus}
							>
								{savingStatus
									? i18n.t("googleAgendaReport.saving")
									: i18n.t("googleAgendaReport.saveStatus")}
							</button>
						</div>
					</div>
				}
			>
				{selected && (
					<div className="space-y-4">
						<div className="overflow-hidden rounded-xl border border-default-200 bg-gradient-to-br from-default-50 to-default-100/80 p-4 dark:border-default-700 dark:from-default-900 dark:to-default-800">
							<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
								<span
									className={`inline-flex rounded-md px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(
										selected.status
									)}`}
								>
									{statusLabel(selected.status)}
								</span>
								<span className="text-xs font-medium text-default-500">
									{formatSourceLabel(selected)}
								</span>
							</div>
							<div className="grid gap-3 sm:grid-cols-2">
								<DetailItem label={i18n.t("googleAgendaReport.colStart")}>
									{formatDateTime(selected.start)}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colEnd")}>
									{formatDateTime(selected.end)}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colName")}>
									{selected.contactName || "—"}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colPhone")}>
									{selected.contactPhone || "—"}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colTicket")}>
									{selected.ticketId != null ? `#${selected.ticketId}` : "—"}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colSource")}>
									{formatSourceLabel(selected)}
								</DetailItem>
								<DetailItem label={i18n.t("googleAgendaReport.colChannel")}>
									{formatChannelLabel(selected.acquisitionChannel, "")}
								</DetailItem>
								{selected.referredBy ? (
									<DetailItem label={i18n.t("googleAgendaReport.colReferredBy")}>
										{selected.referredBy}
									</DetailItem>
								) : null}
								{selected.reminderScheduleId ? (
									<DetailItem label={i18n.t("googleAgendaReport.reminderScheduled")}>
										#{selected.reminderScheduleId}
									</DetailItem>
								) : null}
							</div>
							{selected.description ? (
								<div className="mt-3 border-t border-default-200 pt-3 dark:border-default-700">
									<DetailItem label={i18n.t("googleAgendaReport.colDescription")}>
										<p className="mb-0 whitespace-pre-wrap">{selected.description}</p>
									</DetailItem>
								</div>
							) : null}
						</div>

						{(selected.statusChangedByName || selected.statusReason) && (
							<div className="rounded-lg border border-default-200 bg-card px-3 py-2.5 text-sm dark:border-default-700 dark:bg-default-900">
								{selected.statusChangedByName ? (
									<p className="mb-1 mt-0 text-default-700 dark:text-default-200">
										<span className="font-semibold">
											{String(selected.status).toLowerCase() === "postponed"
												? i18n.t("googleAgendaReport.postponedBy")
												: i18n.t("googleAgendaReport.changedBy")}
											:
										</span>{" "}
										{selected.statusChangedByName}
										{selected.statusChangedAt
											? ` · ${formatDateTime(selected.statusChangedAt)}`
											: ""}
									</p>
								) : null}
								{selected.statusReason ? (
									<p className="mb-0 mt-0 text-default-600 dark:text-default-300">
										<span className="font-semibold">
											{i18n.t("googleAgendaReport.reason")}:
										</span>{" "}
										{selected.statusReason}
									</p>
								) : null}
							</div>
						)}

						<div className="space-y-2 rounded-lg border border-default-200 p-3 dark:border-default-700">
							<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
								{i18n.t("googleAgendaReport.changeStatus")}
								<select
									className="form-select mt-1"
									value={editStatus}
									onChange={(e) => setEditStatus(e.target.value)}
								>
									<option value="scheduled">
										{i18n.t("googleAgendaReport.statusScheduled")}
									</option>
									<option value="completed">
										{i18n.t("googleAgendaReport.statusCompleted")}
									</option>
									<option value="postponed">
										{i18n.t("googleAgendaReport.statusPostponed")}
									</option>
									<option value="cancelled">
										{i18n.t("googleAgendaReport.statusCancelled")}
									</option>
								</select>
							</label>
							{editStatus === "postponed" ? (
								<>
									<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
										{i18n.t("googleAgendaReport.newStart")}
										<input
											type="datetime-local"
											className="form-input mt-1"
											value={editStart}
											onChange={(e) => setEditStart(e.target.value)}
											required
										/>
									</label>
									<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
										{i18n.t("googleAgendaReport.newEnd")}
										<input
											type="datetime-local"
											className="form-input mt-1"
											value={editEnd}
											onChange={(e) => setEditEnd(e.target.value)}
											required
										/>
									</label>
								</>
							) : null}
							<label className="block text-xs font-semibold text-default-600 dark:text-default-300">
								{i18n.t("googleAgendaReport.reasonOptional")}
								<textarea
									className="form-textarea mt-1 min-h-[4.5rem]"
									value={editReason}
									onChange={(e) => setEditReason(e.target.value)}
									placeholder={i18n.t("googleAgendaReport.reasonPlaceholder")}
								/>
							</label>
						</div>
					</div>
				)}
			</AppModal>
		</div>
	);
}
